"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.chooseVariationFromContext = exports.mergeBookendsVariant = exports.filterNodesBySchedule = exports.getAllTestCombinations = exports.getTestsFromCombinationKey = exports.createTestCombinationKey = void 0;
const merge = require("lodash.merge");
const constants_1 = require("./constants");
/**
 *
 * @param tests list of experiment/variation combinations to represent
 * @param startDate UTC time at which the combination is valid for use
 * @param endDate UTC time at which the combination should no longer be used
 * @returns string key to represent the bookends context
 */
const createTestCombinationKey = (tests, startDate, endDate) => {
    let key = "";
    if (tests.length === 0) {
        key = constants_1.DEFAULT_KEY;
    }
    else {
        tests.sort((test1, test2) => (test1.Experiment > test2.Experiment ? 1 : -1));
        key = tests.reduce((key, test) => `${key}${key ? constants_1.EXPERIMENT_SERIALIZATION_SEPARATOR : ""}${test.Experiment}${constants_1.EXPERIMENT_VARIATION_SEPARATOR}${test.Variation}`, "");
    }
    if (startDate || endDate) {
        //Deserialization of a json stringified date gives a string rather than date, so it is better to stick with just keys rather than expect a consistent type
        const additionalContext = JSON.stringify({ [constants_1.START_DATE_KEY]: startDate === null || startDate === void 0 ? void 0 : startDate.toUTCString(), [constants_1.END_DATE_KEY]: endDate === null || endDate === void 0 ? void 0 : endDate.toUTCString() });
        key = `${key}${constants_1.SERIALIZED_COMPONENT_SEPARATOR}${additionalContext}`;
    }
    return key;
};
exports.createTestCombinationKey = createTestCombinationKey;
const getTestsFromCombinationKey = (key) => {
    if (key.startsWith(constants_1.DEFAULT_KEY)) {
        return [];
    }
    const separatorIndex = key.indexOf(constants_1.SERIALIZED_COMPONENT_SEPARATOR);
    return key.substring(0, separatorIndex >= 0 ? separatorIndex : key.length)
        .split(constants_1.EXPERIMENT_SERIALIZATION_SEPARATOR)
        .map(serializedTest => {
        const testData = serializedTest.split(constants_1.EXPERIMENT_VARIATION_SEPARATOR);
        return { Experiment: testData[0], Variation: testData[1] };
    });
};
exports.getTestsFromCombinationKey = getTestsFromCombinationKey;
const getAllTestCombinations = (tests, schedules) => {
    // tests to be the array of test objects, NOT kvps
    const testCombos = {};
    testCombos[(0, exports.createTestCombinationKey)([])] = [];
    for (let firstItem = 0; firstItem < tests.length; firstItem++) {
        for (let comboSize = 1; comboSize + firstItem <= tests.length; comboSize++) {
            for (let itemsFromStart = 1; itemsFromStart <= comboSize; itemsFromStart++) {
                for (let remainderOffset = firstItem + itemsFromStart; remainderOffset + comboSize - itemsFromStart <= tests.length; remainderOffset++) {
                    const firstSection = tests.slice(firstItem, firstItem + itemsFromStart);
                    const secondSection = tests.slice(remainderOffset, remainderOffset + comboSize - itemsFromStart);
                    const combination = [...firstSection, ...secondSection].reduce((prevTests, currTest) => {
                        if (!prevTests.some(test => test.Experiment === currTest.Experiment)) {
                            prevTests.push(currTest);
                        }
                        return prevTests;
                    }, []);
                    testCombos[(0, exports.createTestCombinationKey)(combination)] = combination;
                }
            }
        }
    }
    const results = {};
    Object.values(testCombos).forEach((tests) => {
        results[(0, exports.createTestCombinationKey)(tests)] = { tests, schedule: {} };
        schedules.forEach(schedule => {
            results[(0, exports.createTestCombinationKey)(tests, schedule.startDate, schedule.endDate)] = { tests, schedule };
        });
    });
    return results;
};
exports.getAllTestCombinations = getAllTestCombinations;
const removeChildren = (navData, nodeId) => {
    var _a;
    const navNodeChildren = (_a = navData[nodeId]) === null || _a === void 0 ? void 0 : _a.children;
    if (navNodeChildren === null || navNodeChildren === void 0 ? void 0 : navNodeChildren.length) {
        navNodeChildren.forEach((childNodeId) => {
            removeChildren(navData, childNodeId);
        });
    }
    delete navData[nodeId];
};
const filterNodesBySchedule = (bookendsData, generatedTimeStamp) => {
    if (!generatedTimeStamp || !(bookendsData === null || bookendsData === void 0 ? void 0 : bookendsData.navigation)) {
        return bookendsData;
    }
    // Build up a new navigation that only includes the currently scheduled nodes.
    const filteredNavigation = {};
    const removedNodeIds = [];
    const newTopLevelNavNodes = [];
    let childrenToRemove = [];
    // For each navigation node, decide whether to include it in the new collection
    Object.keys(bookendsData.navigation).forEach((nodeId) => {
        //If we don't have a timestamp to compare against, simply return the unfiltered data.
        const node = bookendsData.navigation[nodeId];
        const generatedTimeStampUTC = generatedTimeStamp === null || generatedTimeStamp === void 0 ? void 0 : generatedTimeStamp.toISOString();
        if ((generatedTimeStampUTC < (node === null || node === void 0 ? void 0 : node.startDate)) || (generatedTimeStampUTC >= (node === null || node === void 0 ? void 0 : node.endDate))) {
            removedNodeIds.push(nodeId);
            // Nodes only appear in one place in the navigation tree, so if we remove a node, we should also remove its children
            if (node.children) {
                childrenToRemove = childrenToRemove.concat(node.children);
            }
            return;
        }
        filteredNavigation[nodeId] = bookendsData.navigation[nodeId];
    });
    // For each node in the filtered navigation, remove filtered nodes from its children array
    Object.keys(filteredNavigation).forEach((nodeId) => {
        removedNodeIds.forEach((removedNodeId) => {
            const removedNodeIndex = filteredNavigation[nodeId].children.indexOf(removedNodeId);
            if (removedNodeIndex > -1) {
                filteredNavigation[nodeId].children.splice(removedNodeIndex, 1);
            }
        });
    });
    // For all the children of removed nodes, remove them and their chidren recursively
    childrenToRemove.forEach((idToRemove) => {
        removeChildren(filteredNavigation, idToRemove);
    });
    // Make a new list of top-level nodes, omitting those removed
    bookendsData.topLevelNavNodes.forEach((nodeId) => {
        if (!removedNodeIds.includes(nodeId)) {
            newTopLevelNavNodes.push(nodeId);
        }
    });
    return Object.assign(Object.assign({}, bookendsData), { navigation: filteredNavigation, topLevelNavNodes: newTopLevelNavNodes });
};
exports.filterNodesBySchedule = filterNodesBySchedule;
const mergeBookendsVariant = (bookendsData, testCombinationKey, generatedTimeStamp) => {
    if (Object.keys(bookendsData['variations']).length === 0) {
        // We have no variations, not even default. Just return the base data.
        return (0, exports.filterNodesBySchedule)(bookendsData['base'], generatedTimeStamp);
    }
    if (!bookendsData['variations'][testCombinationKey]) {
        //if we don't have the requested variant. Substitute the default.
        testCombinationKey = 'default';
    }
    const mergedHeaderData = JSON.parse(JSON.stringify((bookendsData['base'])));
    const variantData = bookendsData['variations'][testCombinationKey];
    if (!variantData || Object.keys(variantData).length === 0) {
        return (0, exports.filterNodesBySchedule)(mergedHeaderData, generatedTimeStamp);
    }
    Object.keys(variantData).forEach((key) => {
        if (Array.isArray(variantData[key])) {
            // If the variant has an array member, merge it into the mergedHeaderData
            if (!mergedHeaderData[key]) {
                // If the base data does not have this array, initialize it.
                mergedHeaderData[key] = [];
            }
            variantData[key].forEach((element) => {
                // Append everything from the variant into the merged data
                if (!mergedHeaderData[key].includes(element)) {
                    mergedHeaderData[key].push(element);
                }
            });
            return;
        }
        if (typeof variantData[key] === "object") {
            merge(mergedHeaderData[key], variantData[key]);
            return;
        }
        // All other members of the variant can be copied to the merged data 
        mergedHeaderData[key] = variantData[key];
    });
    return (0, exports.filterNodesBySchedule)(mergedHeaderData, generatedTimeStamp);
};
exports.mergeBookendsVariant = mergeBookendsVariant;
const chooseVariationFromContext = (bookendsTests, customerTests, buildTime, bookendsVariations) => {
    const experimentContext = (0, exports.createTestCombinationKey)(bookendsTests.filter(test => customerTests.some(cTest => test.Experiment === cTest.Experiment && test.Variation === cTest.Variation)));
    const relevantVariations = bookendsVariations.reduce((acc, variation) => {
        if (variation === experimentContext || variation.startsWith(experimentContext + constants_1.SERIALIZED_COMPONENT_SEPARATOR)) {
            const contextIndex = variation.indexOf(constants_1.SERIALIZED_COMPONENT_SEPARATOR);
            if (contextIndex === -1) {
                acc.push({ key: variation });
            }
            else {
                const additionalContext = JSON.parse(variation.substring(contextIndex + 1));
                const startDate = additionalContext[constants_1.START_DATE_KEY] ? new Date(additionalContext[constants_1.START_DATE_KEY]).valueOf() : undefined;
                const endDate = additionalContext[constants_1.END_DATE_KEY] ? new Date(additionalContext[constants_1.END_DATE_KEY]).valueOf() : undefined;
                if ((!startDate || buildTime.valueOf() >= startDate) && (!endDate || buildTime.valueOf() < endDate)) {
                    acc.push({
                        key: variation,
                        startDate,
                        endDate
                    });
                }
            }
        }
        return acc;
    }, []);
    //Sort the array such that latest start dates (undefined considered earliest), then earliest end dates (undefined considered latest) within the same start date are encountered first
    relevantVariations.sort((a, b) => {
        let result = 5;
        if (a.startDate === b.startDate) {
            if (a.endDate === undefined) {
                result = 1; //a is later (or the same as) b, so move a later in the list
            }
            else if (b.endDate === undefined) {
                result = -1; //b is later than a, so leave it in place
            }
            else {
                result = a.endDate - b.endDate; //leave in place if a is earlier, move a if later
            }
        }
        else if (a.startDate === undefined) {
            result = 1; //a is the same or later, so move a later in list
        }
        else if (b.startDate === undefined) {
            result = -1; //b is earlier than a, so move later in list
        }
        else {
            result = b.startDate - a.startDate; //leave in place if a is later, move if a is earlier
        }
        return result;
    });
    return relevantVariations.length > 0 ? relevantVariations[0].key : constants_1.DEFAULT_KEY;
};
exports.chooseVariationFromContext = chooseVariationFromContext;
